import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Paper,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { useAuthStore } from '../../stores/root_store';
import {
  metricsApi,
  type MetricsOverview,
  type MetricsLifecycle,
  type MetricsQuality,
  type AssistantMetric,
  type MentorMetric,
  type MetricsThroughput,
  type PeriodParams,
} from '../../api/metrics_api';

type Period = 'day' | 'week' | 'month';
type SortDir = 'asc' | 'desc';

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatPct(rate: number | null): string {
  if (rate === null || rate === undefined) return '-';
  return `${(rate * 100).toFixed(1)}%`;
}

export const MetricsDashboard = observer(function MetricsDashboard() {
  const authStore = useAuthStore();
  const theme = useTheme();

  const [period, setPeriod] = useState<Period>('week');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<MetricsOverview | null>(null);
  const [lifecycle, setLifecycle] = useState<MetricsLifecycle | null>(null);
  const [quality, setQuality] = useState<MetricsQuality | null>(null);
  const [assistants, setAssistants] = useState<AssistantMetric[]>([]);
  const [mentors, setMentors] = useState<MentorMetric[]>([]);
  const [throughput, setThroughput] = useState<MetricsThroughput | null>(null);

  // Sorting state for tables
  const [assistantSort, setAssistantSort] = useState<{ field: keyof AssistantMetric; dir: SortDir }>({ field: 'tasks_completed', dir: 'desc' });
  const [mentorSort, setMentorSort] = useState<{ field: keyof MentorMetric; dir: SortDir }>({ field: 'total_reviews', dir: 'desc' });

  const isManager = authStore.roles.includes('manager');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params: PeriodParams = { period };

    try {
      const [ov, lc, qa, asst, ment, tp] = await Promise.all([
        metricsApi.getOverview(params),
        metricsApi.getLifecycle(params),
        metricsApi.getQuality(params),
        metricsApi.getAssistants(params),
        metricsApi.getMentors(params),
        metricsApi.getThroughput(params),
      ]);
      setOverview(ov);
      setLifecycle(lc);
      setQuality(qa);
      setAssistants(asst.assistants);
      setMentors(ment.mentors);
      setThroughput(tp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (!isManager) {
    return <Alert severity="error">Manager access required.</Alert>;
  }

  // Sort helpers
  const sortedAssistants = [...assistants].sort((a, b) => {
    const av = a[assistantSort.field] ?? -Infinity;
    const bv = b[assistantSort.field] ?? -Infinity;
    return assistantSort.dir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const sortedMentors = [...mentors].sort((a, b) => {
    const av = a[mentorSort.field] ?? -Infinity;
    const bv = b[mentorSort.field] ?? -Infinity;
    return mentorSort.dir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const handleAssistantSort = (field: keyof AssistantMetric) => {
    setAssistantSort(prev =>
      prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }
    );
  };

  const handleMentorSort = (field: keyof MentorMetric) => {
    setMentorSort(prev =>
      prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }
    );
  };

  // Lifecycle chart data
  const lifecycleChartData = lifecycle
    ? Object.entries(lifecycle.phases).map(([_key, phase]) => ({
        name: phase.label,
        avg: phase.avg ? Math.round(phase.avg / 60) : 0,
        median: phase.median ? Math.round(phase.median / 60) : 0,
        p95: phase.p95 ? Math.round(phase.p95 / 60) : 0,
      }))
    : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Metrics Dashboard</Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
            <MenuItem value="day">Last 24h</MenuItem>
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Overview Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <OverviewCard label="Mentor Queue" value={overview?.mentor_queue_depth ?? 0} />
            <OverviewCard label="In Review" value={overview?.mentor_in_review ?? 0} />
            <OverviewCard label="Tasks Claimed" value={overview?.period_stats.tasks_claimed ?? 0} />
            <OverviewCard label="Responses" value={overview?.period_stats.responses_submitted ?? 0} />
            <OverviewCard label="Approvals" value={overview?.period_stats.mentor_approvals ?? 0} />
            <OverviewCard label="Denials" value={overview?.period_stats.mentor_denials ?? 0} />
            <OverviewCard label="Tasks Solved" value={overview?.period_stats.tasks_solved ?? 0} />
            <OverviewCard label="Approval Rate" value={formatPct(quality?.approval_rate ?? null)} />
          </Box>

          {/* Lifecycle Chart */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Lifecycle Timing (minutes)</Typography>
              {lifecycleChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={lifecycleChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="avg" name="Average" fill={theme.palette.primary.main} />
                    <Bar dataKey="median" name="Median" fill={theme.palette.secondary.main} />
                    <Bar dataKey="p95" name="P95" fill={theme.palette.warning.main} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary">No lifecycle data yet.</Typography>
              )}
            </CardContent>
          </Card>

          {/* Throughput Chart */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Daily Throughput</Typography>
              {throughput && throughput.daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={throughput.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="solved" name="Solved" stroke={theme.palette.success.main} />
                    <Line type="monotone" dataKey="claimed" name="Claimed" stroke={theme.palette.primary.main} />
                    <Line type="monotone" dataKey="submitted" name="Submitted" stroke={theme.palette.secondary.main} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary">No throughput data yet.</Typography>
              )}
            </CardContent>
          </Card>

          {/* Quality: Approval Rate + Denial Reasons */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quality</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                  <Typography>Total Decisions: {quality?.total_decisions ?? 0}</Typography>
                  <Typography>Approvals: {quality?.approvals ?? 0}</Typography>
                  <Typography>Denials: {quality?.denials ?? 0}</Typography>
                  <Typography>Approval Rate: {formatPct(quality?.approval_rate ?? null)}</Typography>
                  <Typography>Escalations: {quality?.escalations ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Top Denial Reasons</Typography>
                  {quality && quality.denial_reasons.length > 0 ? (
                    quality.denial_reasons.slice(0, 10).map((dr, i) => (
                      <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                        {dr.count}x - {dr.reason}
                      </Typography>
                    ))
                  ) : (
                    <Typography color="text.secondary">No denials in this period.</Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Denial Pattern Analysis */}
          {quality && quality.denial_reasons.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Denial Patterns</Typography>
                <ResponsiveContainer width="100%" height={Math.max(200, quality.denial_reasons.slice(0, 10).length * 40)}>
                  <BarChart
                    data={quality.denial_reasons.slice(0, 10).map(dr => ({
                      reason: dr.reason.length > 40 ? dr.reason.slice(0, 40) + '...' : dr.reason,
                      count: dr.count,
                    }))}
                    layout="vertical"
                    margin={{ left: 200 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="reason" type="category" tick={{ fontSize: 11 }} width={190} />
                    <Tooltip />
                    <Bar dataKey="count" name="Occurrences" fill={theme.palette.error.main} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Assistant Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Assistants</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <AssistantSortCell field="approval_rate" label="Approval Rate" sort={assistantSort} onSort={handleAssistantSort} />
                      <AssistantSortCell field="approvals" label="Approvals" sort={assistantSort} onSort={handleAssistantSort} />
                      <AssistantSortCell field="denials" label="Denials" sort={assistantSort} onSort={handleAssistantSort} />
                      <AssistantSortCell field="avg_response_time" label="Avg Response" sort={assistantSort} onSort={handleAssistantSort} />
                      <AssistantSortCell field="tasks_completed" label="Completed" sort={assistantSort} onSort={handleAssistantSort} />
                      <AssistantSortCell field="claims" label="Claims" sort={assistantSort} onSort={handleAssistantSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAssistants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary" align="center">No assistant data yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedAssistants.map((a) => (
                        <TableRow key={a.key}>
                          <TableCell>{a.name}</TableCell>
                          <TableCell>{formatPct(a.approval_rate)}</TableCell>
                          <TableCell>{a.approvals}</TableCell>
                          <TableCell>{a.denials}</TableCell>
                          <TableCell>{formatDuration(a.avg_response_time)}</TableCell>
                          <TableCell>{a.tasks_completed}</TableCell>
                          <TableCell>{a.claims}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Mentor Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Mentors</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <MentorSortCell field="approval_rate" label="Approval Rate" sort={mentorSort} onSort={handleMentorSort} />
                      <MentorSortCell field="total_reviews" label="Reviews" sort={mentorSort} onSort={handleMentorSort} />
                      <MentorSortCell field="avg_review_time" label="Avg Review" sort={mentorSort} onSort={handleMentorSort} />
                      <MentorSortCell field="reviews_per_hour" label="Reviews/hr" sort={mentorSort} onSort={handleMentorSort} />
                      <MentorSortCell field="approvals" label="Approvals" sort={mentorSort} onSort={handleMentorSort} />
                      <MentorSortCell field="denials" label="Denials" sort={mentorSort} onSort={handleMentorSort} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedMentors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary" align="center">No mentor data yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedMentors.map((m) => (
                        <TableRow key={m.key}>
                          <TableCell>{m.name}</TableCell>
                          <TableCell>{formatPct(m.approval_rate)}</TableCell>
                          <TableCell>{m.total_reviews}</TableCell>
                          <TableCell>{formatDuration(m.avg_review_time)}</TableCell>
                          <TableCell>{m.reviews_per_hour !== null ? m.reviews_per_hour.toFixed(1) : '-'}</TableCell>
                          <TableCell>{m.approvals}</TableCell>
                          <TableCell>{m.denials}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
});

// Helper components

function OverviewCard({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" color="primary.main">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function AssistantSortCell({
  field,
  label,
  sort,
  onSort,
}: {
  field: keyof AssistantMetric;
  label: string;
  sort: { field: keyof AssistantMetric; dir: SortDir };
  onSort: (f: keyof AssistantMetric) => void;
}) {
  return (
    <TableCell sortDirection={sort.field === field ? sort.dir : false}>
      <TableSortLabel
        active={sort.field === field}
        direction={sort.field === field ? sort.dir : 'asc'}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function MentorSortCell({
  field,
  label,
  sort,
  onSort,
}: {
  field: keyof MentorMetric;
  label: string;
  sort: { field: keyof MentorMetric; dir: SortDir };
  onSort: (f: keyof MentorMetric) => void;
}) {
  return (
    <TableCell sortDirection={sort.field === field ? sort.dir : false}>
      <TableSortLabel
        active={sort.field === field}
        direction={sort.field === field ? sort.dir : 'asc'}
        onClick={() => onSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}
